import request from '@/modules/huyu-framework/backend/utils/request'
import { getToken } from '@/modules/huyu-framework/backend/utils/auth'

/**
 * 语音能力（TTS HTTP / ASR 流式 / KWS 流式）
 * ASR 流式识别与 KWS 流式唤醒均走 WebSocket：/huyuAI/speech/stream
 */

// 语音合成：文本 → WAV 音频 Blob
export async function speechTts(text) {
  const token = getToken()
  const response = await fetch(import.meta.env.VITE_APP_BASE_API + '/huyuAI/speech/tts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
    },
    body: JSON.stringify({ text })
  })
  if (!response.ok) {
    throw new Error('语音合成失败: HTTP ' + response.status)
  }
  // 校验响应确为音频：后端认证失败时可能返回 HTTP 200 + JSON 错误体，
  // 若直接当音频播放会报 "no supported source"，需在此显式抛错
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('audio/')) {
    let detail = ''
    try {
      const parsed = JSON.parse(await response.text())
      detail = parsed.msg || parsed.message || ''
    } catch (e) { /* ignore */ }
    throw new Error('语音合成失败: ' + (detail || '服务端未返回有效音频，请检查登录状态'))
  }
  return await response.blob()
}

// 唤醒词校验/预注册：前端设置唤醒词时调用，后端转拼音并构建流式引擎，非法唤醒词返回失败
export function speechKwsValidate(keywords) {
  return request({
    url: '/huyuAI/speech/kws/validate',
    method: 'post',
    data: { keywords },
    timeout: 30000
  })
}

// 语音配置（下发前端：实现类型、唤醒词）
export function getSpeechConfig() {
  return request({
    url: '/huyuAI/speech/config',
    method: 'get',
    timeout: 10000
  })
}

// 说明：TTS 流式播放内核已迁至 src/utils/ttsPlayer.js（段常驻可 seek），
// 状态统一由 Pinia store src/store/modules/tts.js 提供，本文件只保留接口定义与 ASR/KWS。

// ==================== 语音流式 WebSocket ====================
// 构建 WS 地址：token 走 URL 参数；支持 vite 代理相对路径（/serverApi）与绝对地址
function buildSpeechWsUrl() {
  const token = getToken()
  const base = import.meta.env.VITE_APP_BASE_API || ''
  let wsBase
  if (/^https?:\/\//.test(base)) {
    wsBase = base.replace(/^http/, 'ws')
  } else {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    wsBase = proto + '://' + window.location.host + base
  }
  return wsBase + '/huyuAI/speech/stream?token=' + encodeURIComponent(token || '')
}

// 创建语音流（mode: asr/kws）：发 JSON 控制帧 → 持续发二进制 PCM 帧 → 收 JSON 事件
function createSpeechStream({ mode, keywords, onEvent, onError }) {
  let ws
  const state = { closed: false }
  function close() {
    if (state.closed) return
    state.closed = true
    try { if (ws) ws.close() } catch (e) { /* ignore */ }
  }
  ws = new WebSocket(buildSpeechWsUrl())
  ws.binaryType = 'arraybuffer'
  ws.onopen = () => {
    const ctrl = { mode }
    if (mode === 'kws' && keywords) ctrl.keywords = keywords
    try { ws.send(JSON.stringify(ctrl)) } catch (e) { /* ignore */ }
  }
  ws.onmessage = (ev) => {
    if (typeof ev.data !== 'string') return
    try {
      const msg = JSON.parse(ev.data)
      if (onEvent) onEvent(msg)
    } catch (e) { /* ignore */ }
  }
  ws.onerror = () => {
    if (onError) onError(new Error('语音流连接异常'))
  }
  ws.onclose = () => { state.closed = true }
  return {
    // Float32 → Int16 小端 PCM 二进制帧
    sendPcm(float32) {
      if (state.closed || !ws || ws.readyState !== WebSocket.OPEN) return
      const buf = new ArrayBuffer(float32.length * 2)
      const view = new DataView(buf)
      for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]))
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
      }
      ws.send(buf)
    },
    sendStop() {
      if (!state.closed && ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ action: 'stop' })) } catch (e) { /* ignore */ }
      }
    },
    close
  }
}

// ==================== 常驻唤醒监听（KWS 流式）====================
// WebSocket 长连接持续推流，服务端逐帧检测，命中唤醒词即时回推
let kwsStream = null
let kwsMic = null

export function startKwsListening({ keywords, onWake, onError } = {}) {
  stopKwsListening()
  kwsStream = createSpeechStream({
    mode: 'kws',
    keywords: keywords || '',
    onEvent: (ev) => {
      if (ev.type === 'wake') {
        const keyword = ev.keyword || keywords || ''
        stopKwsListening()
        if (onWake) onWake(keyword)
      } else if (ev.type === 'error') {
        stopKwsListening()
        if (onError) onError(new Error(ev.message || '唤醒失败'))
      }
    },
    onError: (e) => {
      stopKwsListening()
      if (onError) onError(e)
    }
  })
  // 打开麦克风持续采集并推流
  ;(async () => {
    let audioContext = null
    let stream = null
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 })
      if (audioContext.state === 'suspended') await audioContext.resume()
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!kwsStream) {
        stream.getTracks().forEach(t => t.stop())
        if (audioContext.close) audioContext.close()
        return
      }
      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(2048, 1, 1)
      processor.onaudioprocess = (e) => {
        if (kwsStream) kwsStream.sendPcm(e.inputBuffer.getChannelData(0))
      }
      source.connect(processor)
      processor.connect(audioContext.destination)
      kwsMic = { audioContext, stream, source, processor }
    } catch (e) {
      stopKwsListening()
      if (onError) onError(e)
    }
  })()
}

export function stopKwsListening() {
  if (kwsStream) { kwsStream.close(); kwsStream = null }
  const mic = kwsMic
  if (mic) {
    kwsMic = null
    try { if (mic.processor) mic.processor.disconnect() } catch (e) { /* ignore */ }
    try { if (mic.source) mic.source.disconnect() } catch (e) { /* ignore */ }
    try { if (mic.stream) mic.stream.getTracks().forEach(t => t.stop()) } catch (e) { /* ignore */ }
    try { if (mic.audioContext && mic.audioContext.close) mic.audioContext.close() } catch (e) { /* ignore */ }
  }
}

// 当前是否在常驻监听中
export function isKwsListening() {
  return !!kwsStream
}

// ==================== VAD 流式 ASR 录音器 ====================
// 前端 RMS 能量判定"是否在说话"用于静音自动结束；识别文本由服务端流式回推（partial/final）。
export function createVadRecorder(opts = {}) {
  const opt = {
    sampleRate: 16000,
    speechThreshold: 0.01, // RMS 能量阈值：低于视为静音（实机按麦克风环境微调）
    silenceMs: 1200,       // 说话后静音超过该时长 → 自动停止
    onPartial: null,       // (text) => void 边说边出字
    onFinal: null,         // (text) => void 最终结果
    onError: null,         // (e) => void
    ...opts
  }
  const state = {
    running: false,
    stopped: false,
    stream: null,
    mic: null,
    vadTimer: null,
    finalTimer: null,
    speeching: false,
    lastSpeechAt: 0
  }

  function frameRms(frame) {
    let sum = 0
    for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i]
    return Math.sqrt(sum / frame.length)
  }

  function cleanupMic() {
    const mic = state.mic
    if (mic) {
      state.mic = null
      try { if (mic.processor) mic.processor.disconnect() } catch (e) { /* ignore */ }
      try { if (mic.source) mic.source.disconnect() } catch (e) { /* ignore */ }
      try { if (mic.stream) mic.stream.getTracks().forEach(t => t.stop()) } catch (e) { /* ignore */ }
      try { if (mic.audioContext && mic.audioContext.close) mic.audioContext.close() } catch (e) { /* ignore */ }
    }
  }

  // 完整清理（幂等）
  function cleanup() {
    if (state.stopped) return
    state.stopped = true
    state.running = false
    if (state.vadTimer) { clearInterval(state.vadTimer); state.vadTimer = null }
    if (state.finalTimer) { clearTimeout(state.finalTimer); state.finalTimer = null }
    if (state.stream) { state.stream.close(); state.stream = null }
    cleanupMic()
  }

  // 请求最终结果：发送 stop，等服务端 final；5s 兜底
  function requestFinal() {
    if (state.stopped) return
    if (state.stream) state.stream.sendStop()
    if (state.finalTimer) clearTimeout(state.finalTimer)
    state.finalTimer = setTimeout(() => {
      if (state.stopped) return
      cleanup()
      if (opt.onFinal) opt.onFinal('')
    }, 5000)
  }

  function start() {
    if (state.running) return Promise.resolve()
    state.stopped = false
    state.running = true
    state.speeching = false
    state.lastSpeechAt = 0
    state.stream = createSpeechStream({
      mode: 'asr',
      onEvent: (ev) => {
        if (ev.type === 'partial') {
          if (opt.onPartial) opt.onPartial(ev.text || '')
        } else if (ev.type === 'final') {
          cleanup()
          if (opt.onFinal) opt.onFinal(ev.text || '')
        } else if (ev.type === 'error') {
          cleanup()
          if (opt.onError) opt.onError(new Error(ev.message || '识别失败'))
        }
      },
      onError: (e) => {
        cleanup()
        if (opt.onError) opt.onError(e)
      }
    })
    return new Promise((resolve, reject) => {
      ;(async () => {
        let audioContext = null
        let micStream = null
        try {
          audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: opt.sampleRate })
          if (audioContext.state === 'suspended') await audioContext.resume()
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
          if (!state.running || !state.stream) {
            micStream.getTracks().forEach(t => t.stop())
            if (audioContext.close) audioContext.close()
            return
          }
          const source = audioContext.createMediaStreamSource(micStream)
          const processor = audioContext.createScriptProcessor(2048, 1, 1)
          processor.onaudioprocess = (e) => {
            if (!state.running || !state.stream) return
            const buf = e.inputBuffer.getChannelData(0)
            state.stream.sendPcm(buf)
            if (frameRms(buf) >= opt.speechThreshold) {
              state.speeching = true
              state.lastSpeechAt = Date.now()
            }
          }
          source.connect(processor)
          processor.connect(audioContext.destination)
          state.mic = { audioContext, stream: micStream, source, processor }
          // VAD 轮询：静音自动结束
          state.vadTimer = setInterval(() => {
            if (!state.running || state.stopped) return
            if (state.speeching && Date.now() - state.lastSpeechAt >= opt.silenceMs) {
              requestFinal()
            }
          }, 300)
          resolve()
        } catch (e) {
          state.running = false
          if (state.stream) { state.stream.close(); state.stream = null }
          try { if (micStream) micStream.getTracks().forEach(t => t.stop()) } catch (e2) { /* ignore */ }
          try { if (audioContext && audioContext.close) audioContext.close() } catch (e2) { /* ignore */ }
          reject(e)
        }
      })()
    })
  }

  // 手动停止：请求最终结果（final 通过 onFinal 回调）
  function stop() {
    requestFinal()
  }

  // 取消：丢弃，不做识别
  function cancel() {
    state.stopped = true
    state.running = false
    if (state.vadTimer) { clearInterval(state.vadTimer); state.vadTimer = null }
    if (state.finalTimer) { clearTimeout(state.finalTimer); state.finalTimer = null }
    if (state.stream) { state.stream.close(); state.stream = null }
    cleanupMic()
  }

  return {
    start,
    stop,
    cancel,
    get isRunning() { return state.running },
    get hasSpeech() { return state.speeching }
  }
}
